import { useCallback, useRef, useState } from "react";
import { MoreHorizontal, Pin, PinOff, Trash2 } from "lucide-react";
import { Icon, useMenuDismiss } from "@cntrl/ui";
import { REACTION_EMOJIS } from "../../app/hooks/inboxFormat.js";
import styles from "./Inbox.module.scss";

export interface MessageActionsProps {
  /** Own messages hang the trigger off the LEFT; everyone else's off the RIGHT — see the note below. */
  mine: boolean;
  onReact: (emoji: string) => void;
  /** Absent when the caller may not delete (anything that is not their own message). */
  onDelete?: () => void;
  /**
   * Pin this message to the top of the chat, or unpin it when it is already the pinned one (C-Pin).
   *
   * Offered on EVERY message, unlike delete: pinning is any participant's (the spec's rule, and
   * deliberately not the rename audience). Absent only where there is nothing to pin TO — a draft.
   */
  onPin?: () => void;
  /** True when THIS message is the one currently pinned, which turns the action into "Unpin". */
  pinned?: boolean;
  /**
   * Hand the press to someone else instead of opening the floating menu (ART-264). A NARROW layout driven by a
   * MOUSE — a desktop window dragged phone-width — has the phone's sheet for its actions but no finger to hold
   * with, so the `…` stays and opens that sheet. Absent, the trigger opens its own row as before.
   */
  onOpen?: () => void;
}

/**
 * The per-message overflow — a `…` bubble that appears on hover and opens the reaction row.
 *
 * **It replaced a permanently-rendered picker.** Every message used to carry all five emoji plus a trash
 * icon underneath it, always, for every message in the thread: five buttons of chrome per line, competing
 * with the words. Reactions are an occasional act and belong behind an affordance.
 *
 * **The trigger sits on the side AWAY FROM THE WALL** — the top-left corner of your own messages (which are
 * right-aligned) and the top-right of everyone else's. The MENU opens below the bubble from its WALL edge, so it
 * grows into the conversation and never off a phone's edge however wide the bubble is (ART-264 — anchored to
 * the trigger, it ran off-screen from a wide bubble's far corner).
 *
 * **`:hover` is not enough on its own.** The row reveals this on `:focus-within` too, or the control is
 * unreachable by keyboard — and once OPEN it stays visible regardless of the pointer (`data-open`), because
 * a menu that vanishes when the mouse leaves the trigger is a menu you cannot walk into.
 *
 * **`useMenuDismiss` rather than a hand-rolled contract**, and it is the case that hook's own doc carves out:
 * take the pieces "when the trigger is a genuinely different control". A horizontal emoji row is not
 * `ActionMenu`'s vertical list of labelled items, but the dismiss behaviour — outside click, Escape that
 * stops at the menu rather than closing the inbox modal behind it, focus moved to the first item — is
 * identical and is the part that is invisible when right and a keyboard trap when wrong.
 */
export function MessageActions({ mine, onReact, onDelete, onPin, pinned, onOpen }: MessageActionsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useMenuDismiss(open, close, rootRef, triggerRef);

  return (
    <div className={styles.msgActions} ref={rootRef} data-mine={mine || undefined} data-open={open || undefined}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.msgActionsTrigger}
        aria-label="Message actions"
        aria-haspopup="menu"
        aria-expanded={onOpen ? undefined : open}
        onClick={() => (onOpen ? onOpen() : setOpen((v) => !v))}
      >
        <Icon icon={MoreHorizontal} size={16} />
      </button>

      {open && (
        <div className={styles.msgActionsMenu} role="menu" aria-label="Message actions">
          <div className={styles.reactionPicker}>
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                role="menuitem"
                className={styles.pickerBtn}
                aria-label={`React ${emoji}`}
                onClick={() => {
                  close();
                  onReact(emoji);
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
          {onPin && (
            <button
              type="button"
              role="menuitem"
              className={styles.menuBtn}
              onClick={() => {
                close();
                onPin();
              }}
            >
              <Icon icon={pinned ? PinOff : Pin} size={16} /> {pinned ? "Unpin" : "Pin"}
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              role="menuitem"
              className={`${styles.menuBtn} ${styles.deleteBtn}`}
              onClick={() => {
                close();
                onDelete();
              }}
            >
              <Icon icon={Trash2} size={16} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
